import type { Place } from "@bandstand/core/arrangeChords";

/** A spot between two letters of the lyrics or two chords, and where to draw the cursor there, in screen pixels. */
export type Caret = {
    place: Place;
    x: number;
    top: number;
    height: number;
};

/**
 * The spot nearest to a point on the screen. It looks at the row of words closest to the point first, and then at
 * the gaps in that row: between any two letters, and before and after the chords. Chords played over the same spot,
 * like `[G][C]grace`, have gaps between them too, drawn next to the chords instead of in the lyrics.
 */
export function caretAt(sheet: HTMLElement, x: number, y: number): Caret | undefined {
    const words = [...sheet.querySelectorAll<HTMLElement>("[data-at]")].map((word) => ({ word, box: word.getBoundingClientRect() }));
    const nearest = Math.min(...words.map(({ box }) => distance(y, box.top, box.bottom)));
    const row = words.filter(({ box }) => distance(y, box.top, box.bottom) === nearest).map(({ word }) => word);
    const off = (caret: Caret) => Math.abs(caret.x - x) + distance(y, caret.top, caret.top + caret.height);

    let best: Caret | undefined;

    for (const caret of row.flatMap((word, index) => [...chordGapsIn(word, row[index + 1]), ...letterGapsIn(word)])) {
        if (!best || off(caret) < off(best)) best = caret;
    }

    return best;
}

/** How far `y` is from the stretch between `top` and `bottom`. */
function distance(y: number, top: number, bottom: number): number {
    return Math.max(0, top - y, y - bottom);
}

/** The gap before a word's chord, and after it when it's the last chord played over its spot. */
function chordGapsIn(word: HTMLElement, next: HTMLElement | undefined): Caret[] {
    const chord = word.querySelector<HTMLElement>("[data-nth]");

    if (!chord) return [];

    const box = chord.getBoundingClientRect();
    const place = { line: Number(word.dataset.line), at: Number(word.dataset.at), nth: Number(chord.dataset.nth) };
    const last = !next || next.dataset.at !== word.dataset.at || !next.querySelector("[data-nth]");
    const before = { place, x: box.left, top: box.top, height: box.height };

    return last ? [before, { ...before, place: { ...place, nth: place.nth + 1 }, x: box.right }] : [before];
}

/** Every gap between the letters of a word, and before and after it. */
function letterGapsIn(word: HTMLElement): Caret[] {
    const text = word.querySelector<HTMLElement>("[data-lyrics]")?.firstChild;
    const line = Number(word.dataset.line);
    const at = Number(word.dataset.at);

    if (!(text instanceof Text) || text.length === 0) return [];

    const range = document.createRange();

    const letter = (index: number) => {
        range.setStart(text, index);
        range.setEnd(text, index + 1);

        return range.getBoundingClientRect();
    };

    const box = text.parentElement!.getBoundingClientRect();

    return Array.from({ length: text.length + 1 }, (_, index) => ({
        place: { line, at: at + index },
        x: index < text.length ? letter(index).left : letter(index - 1).right,
        top: box.top,
        height: box.height,
    }));
}
